import { useCallback, useEffect, useState } from 'react'
import { subscribeToPush, unsubscribeFromPush } from 'services/pushNotifications'

const isWebPushSupported = () => (
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
)

export const useWebPush = () => {
  const [status, setStatus] = useState(isWebPushSupported() ? 'loading' : 'unsupported')
  const [loading, setLoading] = useState(false)

  const refreshStatus = useCallback(async () => {
    if (!isWebPushSupported()) {
      setStatus('unsupported')
      return
    }

    if (Notification.permission === 'denied') {
      setStatus('denied')
      return
    }

    // sem service worker ativo o "ready" nunca termina e o botão ficaria girando
    const registration = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, recusar) => setTimeout(() => recusar(new Error('sem service worker')), 4000)),
    ])
    const subscription = await registration.pushManager.getSubscription()
    setStatus(subscription ? 'subscribed' : 'available')
  }, [])

  useEffect(() => {
    refreshStatus().catch(() => setStatus('available'))
  }, [refreshStatus])

  const enable = useCallback(async () => {
    setLoading(true)
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'denied' : 'available')
        return false
      }

      const registration = await navigator.serviceWorker.ready
      const existingSubscription = await registration.pushManager.getSubscription()
      if (!existingSubscription) await subscribeToPush(registration)
      setStatus('subscribed')
      return true
    } finally {
      setLoading(false)
    }
  }, [])

  const disable = useCallback(async () => {
    setLoading(true)
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) await unsubscribeFromPush(subscription)
      setStatus('available')
    } finally {
      setLoading(false)
    }
  }, [])

  return { status, loading, enable, disable }
}
